import type { MedplumClient } from '@medplum/core';
import type {
  Extension,
  Questionnaire,
  QuestionnaireResponse,
} from '@medplum/fhirtypes';

import {
  AI_PROMPT_KIND_TREATMENT_PLAN_GENERATION,
  AI_PROMPT_KIND_TREATMENT_PLAN_GUIDED,
  EXEMPLAR_TREATMENT_PLAN_QR_SELECTION_EXT,
  EXEMPLAR_TREATMENT_PLAN_TAG,
  EXEMPLAR_TREATMENT_PLAN_TITLE_EXT,
  type AiPromptKind,
} from '@unify/fhir-model/constants';
import { resolveAiPromptText, saveAiPromptText } from '@unify/fhir-model/ai-prompt-binary';
import { resolvePracticePrompt } from './treatmentPlanDefaultPrompts';

export type TreatmentPlanPromptKind = 'guided' | 'generation';

function promptKindCode(kind: TreatmentPlanPromptKind): AiPromptKind {
  if (kind === 'guided') return AI_PROMPT_KIND_TREATMENT_PLAN_GUIDED;
  return AI_PROMPT_KIND_TREATMENT_PLAN_GENERATION;
}

function replaceExtensions(resource: { extension?: Extension[] }, url: string, replacements: Extension[]): void {
  resource.extension = [
    ...(resource.extension ?? []).filter((extension) => extension.url !== url),
    ...replacements,
  ];
}

/** Returns a configured per-template prompt Binary text, if present. */
export async function getTreatmentPlanAiPrompt(
  medplum: MedplumClient,
  questionnaire: Questionnaire,
  kind: TreatmentPlanPromptKind,
): Promise<string | undefined> {
  if (!questionnaire.id) return undefined;
  return resolveAiPromptText(
    medplum,
    promptKindCode(kind),
    `Questionnaire/${questionnaire.id}`,
  );
}

/**
 * Returns the prompt that is actually sent to the AI for this template: the
 * configured override when present, otherwise the built-in default. Bots
 * resolve the same default via `resolvePracticePrompt` — never blank.
 */
export async function getEffectiveTreatmentPlanAiPrompt(
  medplum: MedplumClient,
  questionnaire: Questionnaire,
  kind: TreatmentPlanPromptKind,
): Promise<string> {
  return resolvePracticePrompt(kind, await getTreatmentPlanAiPrompt(medplum, questionnaire, kind));
}

/** Saves a per-template prompt Binary, or clears it when the prompt is blank. */
export async function setTreatmentPlanAiPrompt(
  medplum: MedplumClient,
  questionnaire: Questionnaire,
  kind: TreatmentPlanPromptKind,
  prompt: string | undefined,
): Promise<void> {
  if (!questionnaire.id) {
    throw new Error('Questionnaire id is required to save AI prompts');
  }
  await saveAiPromptText(
    medplum,
    promptKindCode(kind),
    `Questionnaire/${questionnaire.id}`,
    prompt?.trim() || undefined,
  );
}

/** Returns selected exemplar QuestionnaireResponse reference strings in stored order. */
export function getTreatmentPlanExemplarReferences(questionnaire: Questionnaire): string[] {
  return (questionnaire.extension ?? [])
    .filter(
      (extension) =>
        extension.url === EXEMPLAR_TREATMENT_PLAN_QR_SELECTION_EXT
        && Boolean(extension.valueReference?.reference),
    )
    .map((extension) => extension.valueReference!.reference!);
}

/** Replaces selected exemplar references, removing blanks and duplicates. */
export function setTreatmentPlanExemplarReferences(
  questionnaire: Questionnaire,
  references: string[],
): void {
  const uniqueReferences = [...new Set(references.map((reference) => reference.trim()).filter(Boolean))];
  replaceExtensions(
    questionnaire,
    EXEMPLAR_TREATMENT_PLAN_QR_SELECTION_EXT,
    uniqueReferences.map((reference) => ({
      url: EXEMPLAR_TREATMENT_PLAN_QR_SELECTION_EXT,
      valueReference: { reference },
    })),
  );
}

export function getExemplarTreatmentPlanTitle(qr: QuestionnaireResponse): string | undefined {
  return qr.extension?.find((extension) => extension.url === EXEMPLAR_TREATMENT_PLAN_TITLE_EXT)?.valueString;
}

export function setExemplarTreatmentPlanTitle(qr: QuestionnaireResponse, title: string | undefined): void {
  const value = title?.trim();
  replaceExtensions(
    qr,
    EXEMPLAR_TREATMENT_PLAN_TITLE_EXT,
    value ? [{ url: EXEMPLAR_TREATMENT_PLAN_TITLE_EXT, valueString: value }] : [],
  );
}

export function isExemplarTreatmentPlan(qr: QuestionnaireResponse): boolean {
  return (
    qr.meta?.tag?.some(
      (tag) =>
        tag.system === EXEMPLAR_TREATMENT_PLAN_TAG.system
        && tag.code === EXEMPLAR_TREATMENT_PLAN_TAG.code,
    ) ?? false
  );
}

/** Validates that direct patient and encounter links were removed from an exemplar. */
export function validateExemplarHasNoPatientIdentity(qr: QuestionnaireResponse): boolean {
  return qr.subject === undefined && qr.encounter === undefined;
}

/** Creates a new exemplar containing only template linkage, title, tag, and response items. */
export function createDeidentifiedExemplarCopy(
  sourceQr: QuestionnaireResponse,
  title: string,
  questionnaireRef: string,
): QuestionnaireResponse {
  const exemplar: QuestionnaireResponse = {
    resourceType: 'QuestionnaireResponse',
    status: 'completed',
    questionnaire: questionnaireRef,
    meta: { tag: [EXEMPLAR_TREATMENT_PLAN_TAG] },
    item: structuredClone(sourceQr.item ?? []),
  };
  setExemplarTreatmentPlanTitle(exemplar, title);
  return exemplar;
}

/** Search parameters for exemplar treatment plans belonging to one Questionnaire. */
export function buildExemplarTreatmentPlanSearchParams(
  questionnaireRef: string,
): Record<string, string> {
  return {
    questionnaire: questionnaireRef,
    _tag: `${EXEMPLAR_TREATMENT_PLAN_TAG.system}|${EXEMPLAR_TREATMENT_PLAN_TAG.code}`,
    _count: '100',
  };
}

/** Client-side equivalent of the questionnaire + exemplar-tag search filters. */
export function matchesExemplarTreatmentPlanSearch(
  qr: QuestionnaireResponse,
  questionnaireRef: string,
): boolean {
  return qr.questionnaire === questionnaireRef && isExemplarTreatmentPlan(qr);
}
